import { useCallback, useEffect, useState } from "react";
import {
  FiChevronLeft,
  FiChevronRight,
  FiLoader,
  FiRefreshCw,
  FiSave,
  FiSearch,
  FiSlash,
  FiUserCheck,
} from "react-icons/fi";
import AdminUserDetail from "../components/AdminUserDetail";
import {
  getAdminUsers,
  setAdminUserBlocked,
  setAdminUserLevel,
  getAdminHoldPolicy,
  updateAdminHoldPolicy,
} from "../api/admin";
import { adminTheme } from "../styles/adminTheme";

/**
 * Who the users are, what their earnings wait for, and switching one off.
 *
 * A trust level is a hold: a new seller's money waits before they can withdraw it,
 * because a refund or chargeback has to be clawed back from somewhere. Promoting
 * someone shortens that wait, and re-times what they already have waiting.
 *
 * The days per level are edited here too, not buried in the code.
 *
 * @see mintly-back/src/controllers/adminUsersController
 */

const money = (n) => `₾${Number(n || 0).toFixed(2)}`;
const when = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString();
};

const AdminUsersTab = ({ isDark, setToast, baseText, mutedText, cardBase }) => {
  const [users, setUsers] = useState([]);
  const [levels, setLevels] = useState([]);
  const [policy, setPolicy] = useState([]);
  const [policyForm, setPolicyForm] = useState({});
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ q: "", level: "", blocked: "" });
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingPolicy, setSavingPolicy] = useState(false);
  /** The user currently being changed, so only their row shows a spinner. */
  const [busyUser, setBusyUser] = useState(null);
  /** Whose money history is open. */
  const [openUser, setOpenUser] = useState(null);

  const theme = adminTheme(isDark);
  const { inputBase, rowBorder } = theme;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAdminUsers({ ...filters, page, limit: 50 });
      setUsers(res.data || []);
      setLevels(res.levels || []);
      setPages(res.pages || 1);
      setTotal(res.total || 0);
    } catch (err) {
      setToast?.(err?.response?.data?.message || "Could not load users");
    } finally {
      setLoading(false);
    }
  }, [filters, page, setToast]);

  const loadPolicy = useCallback(async () => {
    try {
      const res = await getAdminHoldPolicy();
      setPolicy(res.data || []);
      setPolicyForm(
        Object.fromEntries(
          (res.data || []).map((l) => [
            String(l.level),
            l.configuredHoldDays === null || l.configuredHoldDays === undefined
              ? ""
              : String(l.configuredHoldDays),
          ])
        )
      );
    } catch (err) {
      setToast?.(err?.response?.data?.message || "Could not load the hold policy");
    }
  }, [setToast]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadPolicy();
  }, [loadPolicy]);

  const savePolicy = async () => {
    setSavingPolicy(true);
    try {
      const holdDays = Object.fromEntries(
        Object.entries(policyForm).map(([level, value]) => {
          const trimmed = String(value).trim();
          const n = Number(trimmed);
          return [level, trimmed === "" || !Number.isFinite(n) ? null : Math.max(0, n)];
        })
      );
      const res = await updateAdminHoldPolicy(holdDays);
      setPolicy(res.data || []);
      setToast?.("Hold policy saved — it applies to money credited from now on");
      await load();
    } catch (err) {
      setToast?.(err?.response?.data?.message || "Could not save the hold policy");
    } finally {
      setSavingPolicy(false);
    }
  };

  const changeLevel = async (user, level) => {
    setBusyUser(user.id);
    try {
      const res = await setAdminUserLevel(user.id, Number(level));
      const freed = res.holds?.released || 0;
      setToast?.(
        `${user.username} → L${res.trustLevel} (${res.holdDays}-day hold)` +
          (freed ? ` · ${freed} held credit${freed === 1 ? "" : "s"} released` : "")
      );
      await load();
    } catch (err) {
      setToast?.(err?.response?.data?.message || "Could not change the level");
    } finally {
      setBusyUser(null);
    }
  };

  const toggleBlock = async (user) => {
    const blocking = !user.blocked;
    let reason = "";
    if (blocking) {
      reason = window.prompt(
        `Block ${user.username}?\n\nThey won't be able to sign in, and any open session stops working. Reason (optional):`,
        ""
      );
      if (reason === null) return; // cancelled
    } else if (!window.confirm(`Unblock ${user.username}?`)) {
      return;
    }

    setBusyUser(user.id);
    try {
      await setAdminUserBlocked(user.id, blocking, reason || "");
      setToast?.(`${user.username} ${blocking ? "blocked" : "unblocked"}`);
      await load();
    } catch (err) {
      setToast?.(err?.response?.data?.message || "Could not update this user");
    } finally {
      setBusyUser(null);
    }
  };

  const applySearch = (e) => {
    e.preventDefault();
    setPage(1);
    setFilters((prev) => ({ ...prev, q: search.trim() }));
  };

  const setFilter = (key, value) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="space-y-6">
      {/* ----------------------------------------------------- hold policy */}
      <div className={`${cardBase} rounded-2xl p-5 sm:p-6`}>
        <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
          <div>
            <h3 className={`text-base font-semibold tracking-tight ${baseText}`}>Hold policy</h3>
            <p className={`text-xs ${mutedText} mt-1 max-w-2xl`}>
              How long a seller's earnings wait before they can withdraw them. The hold
              is what a refund or chargeback is clawed back from, so a new account waits
              longest. Leave a field empty to use the default.
            </p>
          </div>
          <button
            type="button"
            onClick={savePolicy}
            disabled={savingPolicy}
            className={theme.btnPrimary}
          >
            {savingPolicy ? <FiLoader className="h-4 w-4 animate-spin" /> : <FiSave className="h-4 w-4" />}
            Save policy
          </button>
        </div>

        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {policy.map((l) => (
            <div key={l.level} className={`rounded-xl p-4 ${theme.inset}`}>
              <div className={`text-sm font-bold ${baseText}`}>
                {l.key} — {l.label}
              </div>
              <div className="mt-2 flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="365"
                  step="1"
                  value={policyForm[String(l.level)] ?? ""}
                  onChange={(e) =>
                    setPolicyForm((prev) => ({ ...prev, [String(l.level)]: e.target.value }))
                  }
                  placeholder={String(l.holdDays)}
                  aria-label={`${l.key} hold days`}
                  className={`w-20 rounded-xl border px-3 py-2 text-sm outline-none ${inputBase}`}
                />
                <span className={`text-sm ${mutedText}`}>days</span>
              </div>
              <p className={`mt-2 text-[11px] ${mutedText}`}>
                {l.effectiveHoldDays === 0 ? "paid out at once" : `${l.effectiveHoldDays}-day hold`}
                {" · "}
                {l.users} {l.users === 1 ? "user" : "users"}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* ---------------------------------------------------------- users */}
      <div className={`${cardBase} rounded-2xl p-5 sm:p-6`}>
        <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
          <div>
            <h3 className={`text-base font-semibold tracking-tight ${baseText}`}>Users ({total})</h3>
            <p className={`text-xs ${mutedText} mt-1`}>
              Changing someone's level also re-times the money they already have waiting.
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            className={`${theme.btnSecondary}`}
          >
            <FiRefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-5">
          <form onSubmit={applySearch} className="relative flex-1 min-w-[240px]">
            <FiSearch className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${mutedText}`} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Username, email or business"
              className={`w-full rounded-xl border pl-9 pr-3 py-2 text-sm outline-none ${inputBase}`}
            />
          </form>
          <select
            value={filters.level}
            onChange={(e) => setFilter("level", e.target.value)}
            className={`rounded-xl border px-3 py-2 text-sm outline-none ${inputBase}`}
          >
            <option value="">All levels</option>
            {levels.map((l) => (
              <option key={l.level} value={l.level}>
                {l.key} — {l.label}
              </option>
            ))}
          </select>
          <select
            value={filters.blocked}
            onChange={(e) => setFilter("blocked", e.target.value)}
            className={`rounded-xl border px-3 py-2 text-sm outline-none ${inputBase}`}
          >
            <option value="">Everyone</option>
            <option value="false">Active only</option>
            <option value="true">Blocked only</option>
          </select>
        </div>

        <div className={theme.tableWrap}>
          <table className="w-full text-sm">
            <thead>
              <tr className={`text-left ${theme.thead}`}>
                {["User", "Joined", "Balance", "Level", ""].map((h) => (
                  <th key={h} className={theme.th}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className={`border-t ${rowBorder} ${theme.rowHover} align-top`}>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => setOpenUser(u.id)}
                      className={`text-left font-semibold ${baseText} hover:text-mint`}
                    >
                      {u.username || u.businessName || "—"}
                      {u.blocked && (
                        <span className="ml-2 rounded-lg bg-rose-500/15 px-2 py-0.5 text-[11px] font-semibold text-rose-400">
                          blocked
                        </span>
                      )}
                      {u.role === "admin" && (
                        <span className={`ml-2 text-[11px] ${mutedText}`}>admin</span>
                      )}
                    </button>
                    <div className={`text-[11px] ${mutedText}`}>{u.email}</div>
                    {u.blocked && u.blockedReason && (
                      <div className="text-[11px] text-rose-400">{u.blockedReason}</div>
                    )}
                  </td>
                  <td className={`px-4 py-3 whitespace-nowrap ${mutedText}`}>
                    <div>{when(u.createdAt)}</div>
                    <div className="text-[11px]">{u.accountType}</div>
                  </td>
                  <td className={`px-4 py-3 whitespace-nowrap ${baseText}`}>
                    <div>{money(u.balance?.available)}</div>
                    {u.balance?.onHold > 0 && (
                      <div className={`text-[11px] ${mutedText}`}>
                        {money(u.balance.onHold)} on hold
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={u.trustLevel}
                      disabled={busyUser === u.id}
                      onChange={(e) => changeLevel(u, e.target.value)}
                      aria-label={`${u.username} trust level`}
                      className={`rounded-xl border px-2 py-1.5 text-xs outline-none disabled:opacity-50 ${inputBase}`}
                    >
                      {levels.map((l) => (
                        <option key={l.level} value={l.level}>
                          {l.key} — {l.label}
                        </option>
                      ))}
                    </select>
                    <div className={`mt-1 text-[11px] ${mutedText}`}>
                      {u.holdDays === 0 ? "no hold" : `${u.holdDays}-day hold`}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => toggleBlock(u)}
                      disabled={busyUser === u.id}
                      className={`flex items-center gap-2 rounded-xl border px-3 py-1.5 text-xs font-semibold transition disabled:opacity-50 ${rowBorder} ${
                        u.blocked ? "text-mint" : "text-rose-400"
                      }`}
                    >
                      {busyUser === u.id ? (
                        <FiLoader className="h-3.5 w-3.5 animate-spin" />
                      ) : u.blocked ? (
                        <FiUserCheck className="h-3.5 w-3.5" />
                      ) : (
                        <FiSlash className="h-3.5 w-3.5" />
                      )}
                      {u.blocked ? "Unblock" : "Block"}
                    </button>
                  </td>
                </tr>
              ))}
              {!loading && users.length === 0 && (
                <tr>
                  <td colSpan={5} className={`py-10 text-center ${mutedText}`}>
                    No users match this filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between mt-4">
          <span className={`text-xs ${mutedText}`}>
            Page {page} of {pages}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className={`${theme.iconBtn} disabled:opacity-40`}
              aria-label="Previous page"
            >
              <FiChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              disabled={page >= pages}
              className={`${theme.iconBtn} disabled:opacity-40`}
              aria-label="Next page"
            >
              <FiChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {openUser && (
        <AdminUserDetail
          userId={openUser}
          isDark={isDark}
          setToast={setToast}
          onClose={() => setOpenUser(null)}
        />
      )}
    </div>
  );
};

export default AdminUsersTab;
